import { contacts } from '@/data/contacts';
import { useLanguage } from '@/shared/contexts/LanguageContext';
import { Clock, Mail, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './button';

export function Footer() {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    {
      name: 'GitHub',
      href: contacts.github,
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
        </svg>
      )
    },
    {
      name: 'LinkedIn',
      href: contacts.linkedin,
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      )
    },
    {
      name: 'X (Twitter)',
      href: contacts.twitter,
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      )
    },
    {
      name: 'Bluesky',
      href: contacts.bluesky,
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 64 57" fill="none">
          <path fill="currentColor" d="M13.873 3.805C21.21 9.332 29.103 20.537 32 26.55v15.882c0-.338-.13.044-.41.867-1.512 4.456-7.418 21.847-20.923 7.944-7.111-7.32-3.819-14.64 9.125-16.85-7.405 1.264-15.73-.825-18.014-9.015C1.12 23.022 0 8.51 0 6.55 0-3.268 8.579-.182 13.873 3.805ZM50.127 3.805C42.79 9.332 34.897 20.537 32 26.55v15.882c0-.338.13.044.41.867 1.512 4.456 7.418 21.847 20.923 7.944 7.111-7.32 3.819-14.64-9.125-16.85 7.405 1.264 15.73-.825 18.014-9.015C62.88 23.022 64 8.51 64 6.55c0-9.818-8.578-6.732-13.873-2.745Z"></path>
        </svg>
      )
    },
    {
      name: 'Email',
      href: contacts.emailHref,
      icon: <Mail className="w-5 h-5" />
    }
  ];

  const academicLinks = [
    { name: t('about'), href: '/' },
    { name: t('project'), href: '/project' },
    { name: t('posts'), href: '/posts' },
    { name: t('cv'), href: '/cv' }
  ];

  const personalLinks = [
    { name: t('spaceHome'), href: '/space' },
    { name: t('spacePortfolio'), href: '/space/portfolio' },
    { name: t('spaceBlog'), href: '/space/blog' }
  ];

  return (
    <footer className="mt-16 md:mt-24 border-t border-border/50 bg-background">
      {/* 桌面端布局 */}
      <div className="hidden md:block max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Personal Info Section */}
          <div className="lg:col-span-1">
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl font-light text-foreground mb-3">{t('name')}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {t('footerAffiliation')}<br />
                  {t('footerDegree')}
                </p>
              </div>
              
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Mail className="w-4 h-4" />
                  <a href={contacts.emailHref} className="hover:text-foreground transition-colors">
                    kaibin.zhang.23@ucl.ac.uk
                  </a>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="w-4 h-4" />
                  <span>London, UK</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="w-4 h-4" />
                  <span>UTC+1</span>
                </div>
              </div>
            </div>
          </div>

          {/* Academic Section */}
          <div className="space-y-6">
            <h4 className="text-sm font-medium text-foreground uppercase tracking-wider">{t('footerAcademic')}</h4>
            <ul className="space-y-3">
              {academicLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="group flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <span className="w-1 h-1 bg-primary/30 group-hover:bg-primary/60 rounded-full transition-colors" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Personal Section */}
          <div className="space-y-6">
            <h4 className="text-sm font-medium text-foreground uppercase tracking-wider">{t('footerPersonal')}</h4>
            <ul className="space-y-3">
              {personalLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="group flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <span className="w-1 h-1 bg-primary/30 group-hover:bg-primary/60 rounded-full transition-colors" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Connect Section */}
          <div className="space-y-6">
            <h4 className="text-sm font-medium text-foreground uppercase tracking-wider">{t('footerConnect')}</h4>
            <div className="flex flex-wrap gap-3">
              {socialLinks.map((social) => (
                <Button
                  key={social.name}
                  variant="ghost"
                  size="icon"
                  asChild
                  className="h-10 w-10 rounded-xl border border-border/30 hover:border-border/60 hover:bg-muted/50"
                >
                  <a
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.name}
                  >
                    {social.icon}
                  </a>
                </Button>
              ))}
            </div>
            
            <div className="pt-4">
              <span className="text-sm text-muted-foreground">zkb.bio</span>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-16 pt-8 border-t border-border/30">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">
              © {currentYear} {t('name')}. {t('allRightsReserved')}
            </p>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <span>University College London</span>
              <span className="text-muted-foreground/30">•</span>
              <span>History, Politics and Economics</span>
            </div>
          </div>
        </div>
      </div>

      {/* 移动端精致紧凑布局 */}
      <div className="md:hidden">
        <div className="px-4 py-10 space-y-8">
          {/* 品牌区域 */}
          <div className="space-y-4">
            <div>
              <h3 className="text-xl font-light text-foreground mb-2">{t('name')}</h3>
              <p className="text-sm text-muted-foreground">{t('footerAffiliation')}</p>
              <p className="text-sm text-muted-foreground">{t('footerDegree')}</p>
            </div>
            
            {/* 社交媒体 */}
            <div className="flex gap-2">
              {socialLinks.map((social) => (
                <Button
                  key={social.name}
                  variant="ghost"
                  size="icon"
                  asChild
                  className="h-9 w-9 rounded-lg border border-border/30 hover:border-border/60"
                >
                  <a
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.name}
                  >
                    {social.icon}
                  </a>
                </Button>
              ))}
            </div>
          </div>

          {/* 导航链接 - 两列布局 */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h4 className="text-xs font-medium text-foreground uppercase tracking-wider mb-3">{t('footerAcademic')}</h4>
              <ul className="space-y-2">
                {academicLinks.map((link) => (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h4 className="text-xs font-medium text-foreground uppercase tracking-wider mb-3">{t('footerPersonal')}</h4>
              <ul className="space-y-2">
                {personalLinks.map((link) => (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* 底部信息 */}
        <div className="px-4 py-4 border-t border-border/30 bg-muted/10">
          <div className="flex flex-col items-center gap-2 text-center">
            <p className="text-xs text-muted-foreground">
              © {currentYear} {t('name')}. {t('allRightsReserved')}
            </p>
            <span className="text-xs text-muted-foreground">zkb.bio</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;